import * as React from 'react';
import { Component } from 'react';
import { AppWrapper, ProSeparContainer } from './App.s';
import { Topbar } from '@components/Topbar/Topbar';
import { SectionOne } from '@components/SectionOne/SectionOne';
import { SectionTwo } from '@components/SectionTwo/SectionTwo';
import { SectionPhone } from '@components/SectionPhone/SectionPhone';
import { SectionTeaser } from '@components/SectionTeaser/SectionTeaser';
import { SectionProfessional } from '@components/SectionProfessional/SectionProfessional';
import { SectionCards } from '@components/SectionCards/SectionCards';
import { SectionGenius } from '@components/SectionGenius/SectionGenius';
import { Footer } from '@components/Footer/Footer';
import { SkewImageSeparator } from '@components/SkewImageSeparator/SkewImageSeparator';
import { ProContent } from '@components/ProContent/ProContent';
import { InjectApp, Core } from '@core';
import { History, Location } from 'history';
import { basePath, proPath, discoverPath, aboutPath, explainPath } from '@config/paths';
import Measure from 'react-measure';
import { PathPattern } from 'path-pattern';
import { getScrollTop } from '@utils/index';
import scrollTo = require('scroll-to');
import { ThemeProvider } from 'styled-components';
import throttle = require('lodash/throttle');

export type AppTheme = {
  windowHeight: number;
  windowWidth: number;
};

type InjectProps = {
  app: Core;
};

type AppProps = Partial<InjectProps>;

type AppState = {
  location: Location;
  discoverPosition: number;
  explainPosition: number;
  aboutPosition: number;
  windowHeight: number;
  windowWidth: number;
};

@InjectApp<typeof App, {}>()
export class App extends Component<AppProps, AppState> {
  private history: History;
  private unlisten: () => void;

  constructor(props: AppProps) {
    super(props);

    this.onLocationChange = this.onLocationChange.bind(this);
    this.onResize = throttle(this.onResize.bind(this), 1000 / 30);

    if (props.app) {
      this.history = props.app.getHistory();
    }

    this.state = {
      location: this.history.location,
      discoverPosition: 0,
      explainPosition: 2800,
      aboutPosition: 6200,
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
    };
  }

  componentWillMount() {
    this.unlisten = this.history.listen(this.onLocationChange);
    this.onLocationChange(this.history.location);
    window.addEventListener('resize', this.onResize);
  }

  componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten();
    }
    window.removeEventListener('resize', this.onResize);
  }

  render(): JSX.Element | null {
    const { discoverPosition, explainPosition, aboutPosition, windowHeight, windowWidth, location } = this.state;

    return (
      <ThemeProvider
        theme={() => ({
          windowHeight,
          windowWidth,
        })}
      >
        <AppWrapper>
          <Topbar
            {...{
              discoverPosition,
              explainPosition,
              aboutPosition,
              location,
              windowHeight,
              windowWidth,
            }}
          />
          {this.renderContent()}
          <Footer />
        </AppWrapper>
      </ThemeProvider>
    );
  }

  private renderContent(): JSX.Element | null {
    const { location, windowWidth, windowHeight } = this.state;
    if (
      PathPattern.matchOneOf(
        discoverPath.matchExact,
        aboutPath.matchExact,
        explainPath.matchExact,
        basePath.matchExact
      )(location)
    ) {
      return (
        <>
          <SectionOne windowWidth={windowWidth} />
          <SectionTwo />
          <SectionTeaser />
          <SkewImageSeparator height={550} translate={0} image={require('./medias/separ01.jpg')} margin={50} />
          <Measure
            offset={true}
            onResize={({ offset }) => {
              if (offset) {
                this.setState({ explainPosition: offset.top });
              }
            }}
          >
            {({ measureRef }) => (
              <SectionPhone innerRef={measureRef} windowWidth={windowWidth} windowHeight={windowHeight} />
            )}
          </Measure>
          <SkewImageSeparator height={550} translate={0} image={require('./medias/separ02.jpg')} margin={50} />
          <SectionProfessional />
          <Measure
            offset={true}
            onResize={({ offset }) => {
              if (offset) {
                this.setState({ aboutPosition: offset.top - 50 });
              }
            }}
          >
            {({ measureRef }) => (
              <div ref={measureRef}>
                <SectionCards />,
                <SkewImageSeparator height={900} translate={0} image={require('./medias/separ03.jpg')} margin={50} />,
                <SectionGenius />
              </div>
            )}
          </Measure>
        </>
      );
    }
    if (proPath.matchExact(location)) {
      return (
        <>
          <ProSeparContainer>
            <SkewImageSeparator height={700} translate={200} image={require('./medias/separ04.jpg')} margin={50} />
          </ProSeparContainer>
          <ProContent />
        </>
      );
    }
    // Invalid path => to discover
    this.history.replace(discoverPath.compile());
    return null;
  }

  private onLocationChange(newLocation: Location): void {
    const fromScroll = !!(newLocation.state && newLocation.state.fromScroll);
    if (!fromScroll || this.history.action !== 'PUSH') {
      if (discoverPath.matchExact(newLocation)) {
        this.scrollTo(this.state.discoverPosition + 2);
      } else if (explainPath.matchExact(newLocation)) {
        this.scrollTo(this.state.explainPosition + 2);
      } else if (aboutPath.matchExact(newLocation)) {
        this.scrollTo(this.state.aboutPosition + 2);
      }
    }
    this.setState({ location: newLocation });
  }

  private onResize(): void {
    this.setState({
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
    });
  }

  private scrollTo(target: number): void {
    const isOnePage = PathPattern.matchOneOf(
      discoverPath.matchExact,
      aboutPath.matchExact,
      explainPath.matchExact,
      basePath.matchExact
    )(this.state.location);

    if (target === getScrollTop()) {
      return;
    }
    if (isOnePage) {
      scrollTo(0, target, {
        duration: 1000,
      });
    } else {
      window.setTimeout(() => {
        scrollTo(0, target, {
          duration: 1000,
        });
      }, 50);
    }
  }
}



// WEBPACK FOOTER //
// src/app/src/components/App/App.tsx